import restApi from './http.js'
import session from './services/session'
import router from './router'
import qs from 'qs'

// Flag to prevent multiple refresh requests
let isRefreshing = false
let failedQueue = []

// Process the queue of failed requests
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// Setup function that configures the interceptors
export function setupInterceptors() {
  // Clear existing interceptors to avoid conflicts
  restApi.interceptors.request.handlers = []
  restApi.interceptors.response.handlers = []

  // Add a custom parameter serializer that handles empty arrays as undefined
  restApi.defaults.paramsSerializer = (params) => {
    const transformedParams = { ...params }
    Object.keys(transformedParams).forEach((key) => {
      if (Array.isArray(transformedParams[key]) && transformedParams[key].length === 0) {
        transformedParams[key] = undefined
      }
    })

    // Use qs to serialize with appropriate configuration
    return qs.stringify(transformedParams, {
      arrayFormat: 'brackets',
      skipNulls: true,
      encode: true
    })
  }

  // Add a request interceptor to add token to all requests
  restApi.interceptors.request.use(
    async (config) => {
      // Don't add token to auth endpoints
      if (config.url && config.url.includes('/auth/')) {
        return config
      }

      let token = session.getAccessToken()

      // Check if token is expired and refresh if needed
      if (token && session.isTokenExpired()) {
        try {
          token = await session.refreshAccessToken()
        } catch (error) {
          console.error('Token refresh failed:', error)
        }
      }

      if (token) {
        config.headers = config.headers || {}
        config.headers['Authorization'] = 'Bearer ' + token
      }

      return config
    },
    (error) => {
      console.error('Request interceptor error:', error)
      return Promise.reject(error)
    }
  )

  // Add a request interceptor to fix parameter format
  restApi.interceptors.request.use(
    async (config) => {
      // Check if the URL contains query parameters with dots
      if (config.url && config.url.includes('?')) {
        // Split URL into base and query parts
        const [baseUrl, queryString] = config.url.split('?')

        // Parse the query string
        const params = {}
        queryString.split('&').forEach((pair) => {
          // Handle empty parameters
          if (!pair) return

          const [key, value] = pair.split('=')
          if (!key) return

          // Check if the key contains a dot (indicating nested parameters)
          if (key.includes('.')) {
            // Extract the parent and child keys
            const [parent, child] = key.split('.')

            // If this is a parameter like 'retailerId.page', transform it
            if (parent && child) {
              // Use the child as the real parameter
              // For example, 'retailerId.page=1' becomes 'page=1'
              params[child] = decodeURIComponent(value || '')
            } else {
              // If it's just a regular parameter with a dot, keep it as is
              params[key] = decodeURIComponent(value || '')
            }
          } else {
            // Regular parameter, keep as is
            params[key] = decodeURIComponent(value || '')
          }
        })

        // Rebuild the query string without the nested notation
        const newQueryString = Object.keys(params)
          .map((key) => `${encodeURIComponent(key)}=${params[key]}`)
          .join('&')
        // Update the URL
        config.url = baseUrl + (newQueryString ? `?${newQueryString}` : '')
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  // First, set up all other response interceptors
  restApi.interceptors.response.use(
    (response) => {
      return response
    },
    async (error) => {
      // Ensure error.config exists to avoid null pointer exceptions
      const originalRequest = error.config || {}
      originalRequest.headers = originalRequest.headers || {}

      // Skip token refresh for auth endpoints - they should return 401 directly for invalid credentials
      if (originalRequest.url && originalRequest.url.includes('/auth/')) {
        return Promise.reject(error)
      }

      // If there's no response, or error is not 401, or request already tried after refresh, reject
      if (!error.response || error.response.status !== 401 || originalRequest._retry) {
        if (!error.response) {
          console.error('No response from server (possibly CORS issue):', error)
        } else if (error.response.status === 401 && originalRequest._retry) {
          console.warn('Request already retried after token refresh, still failing')
        }
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // If already refreshing, add request to queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token
            return restApi(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Try to refresh the token
        const newToken = await session.refreshAccessToken()

        if (newToken) {
          // Update authorization header
          originalRequest.headers['Authorization'] = 'Bearer ' + newToken
          processQueue(null, newToken)
          isRefreshing = false
          return restApi(originalRequest)
        } else {
          throw new Error('Refresh token returned null')
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        processQueue(refreshError)
        // Clear session
        session.destroy()
        // Redirect to login page
        if (router && typeof router.push === 'function') {
          router.push('/sign-in')
        }
        isRefreshing = false
        return Promise.reject(refreshError)
      }
    }
  )
}

// Setup interceptors immediately when this module is imported
setupInterceptors()

// Export the already-configured axios instance
export default restApi
